import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Calendar, Monitor, Users, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next"; // Import hook i18n

export default function LocationVenue() {
  const { t } = useTranslation('public'); // Gunakan namespace 'public'

  return (
    <section className="relative py-24 bg-slate-50 dark:bg-gray-950 overflow-hidden">

      {/* 1. BACKGROUND PATTERN */}
      <div className="absolute inset-0 z-0 opacity-40 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
      </div>

      <div className="relative z-10 container mx-auto px-6 max-w-7xl">

        {/* 2. HEADER */}
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-medium mb-2">
            <MapPin className="w-4 h-4" />
            <span>{t('venue.badge')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 dark:text-white">
            {t('venue.title_main')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">{t('venue.title_highlight')}</span>
          </h2>
          <p className="max-w-2xl mx-auto text-lg text-gray-600 dark:text-gray-400">
            {t('venue.description')}
          </p>
        </div>

        {/* 3. EVENT TYPE CARDS (Grid Atas) */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mb-12">
          {/* Card: Konferensi */}
          <div className="group relative bg-white dark:bg-gray-900 rounded-2xl p-1 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-800">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300" />
            <div className="relative h-full p-6 flex flex-col md:flex-row items-start md:items-center gap-5">
              <div className="flex-shrink-0 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                <Monitor className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1 group-hover:text-blue-600 transition-colors">
                  {t('venue.conference.title')}
                </h3>
                <div className="flex items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400 mb-2">
                  <span className="px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/50 text-xs uppercase tracking-wider">{t('venue.conference.mode')}</span>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  <span className="font-semibold text-gray-900 dark:text-gray-200 block mb-1">{t('hero.conference.date')}</span>
                  {t('venue.conference.detail')}
                </p>
              </div>
            </div>
          </div>

          {/* Card: Konvensi */}
          <div className="group relative bg-white dark:bg-gray-900 rounded-2xl p-1 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-800">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300" />
            <div className="relative h-full p-6 flex flex-col md:flex-row items-start md:items-center gap-5">
              <div className="flex-shrink-0 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                <Calendar className="w-8 h-8 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1 group-hover:text-orange-600 transition-colors">
                  {t('venue.convention.title')}
                </h3>
                <div className="flex items-center gap-2 text-sm font-medium text-orange-600 dark:text-orange-400 mb-2">
                  <span className="px-2 py-0.5 rounded bg-orange-100 dark:bg-orange-900/50 text-xs uppercase tracking-wider">{t('venue.convention.mode')}</span>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  <span className="font-semibold text-gray-900 dark:text-gray-200 block mb-1">{t('hero.convention.date')}</span>
                  {t('venue.convention.detail')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 4. MAIN LAYOUT (Map & Details) */}
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">

          {/* KOLOM KIRI: MAP (Span 7) */}
          <div className="lg:col-span-7 flex flex-col">
            <Card className="flex-1 overflow-hidden rounded-3xl shadow-lg border-0 ring-1 ring-gray-200 dark:ring-gray-800 bg-white dark:bg-gray-900 flex flex-col h-full min-h-[400px]">
              <CardContent className="p-0 flex-1 relative h-full w-full">
                {/* Overlay Gradient */}
                <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-black/30 to-transparent z-10 pointer-events-none" />

                {/* Google Maps Embed */}
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3942.864741492576!2d115.225345710631!3d-8.79973808983995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2dd2431761e1b7db%3A0x7d60920f666b6c0e!2sBali%20Nusa%20Dua%20Convention%20Center!5e0!3m2!1sid!2sid!4v1700000000000!5m2!1sid!2sid"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ border: 0, filter: 'grayscale(0%)' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />

                <div className="absolute bottom-4 right-4 z-20">
                  <a
                    href="https://maps.app.goo.gl/uP63uQG6xS6H9L6H8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-white text-gray-900 px-4 py-2 rounded-full text-xs font-bold shadow-lg hover:bg-gray-50 transition-colors"
                  >
                    {t('venue.maps_button')} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* KOLOM KANAN: INFO (Span 5) */}
          <div className="lg:col-span-5 flex flex-col gap-6 h-full">

            {/* Address Card */}
            <Card className="flex-1 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border border-white/20 dark:border-gray-800 shadow-xl flex flex-col justify-center">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl text-indigo-600 dark:text-indigo-400 shrink-0">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{t('venue.address_title')}</h4>
                    <div className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm">
                      <strong className="block text-gray-900 dark:text-white mb-1 text-base">Bali Nusa Dua Convention Centre (BNDCC)</strong>
                      Kawasan Pariwisata Nusa Dua Blok NW/1,<br />
                      Kec. Kuta Selatan, Kab. Badung,<br />
                      Bali 80363, Indonesia
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Participants Card */}
            <Card className="flex-1 bg-gradient-to-br from-blue-600 to-indigo-700 text-white border-none shadow-xl flex flex-col justify-center">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-white/20 rounded-xl text-white shrink-0">
                    <Users className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold mb-2">{t('venue.participants_title')}</h4>
                    <p className="text-blue-100 leading-relaxed text-sm">
                      {t('venue.participants_desc')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>

      </div>
    </section>
  );
}